/** @jsxImportSource @emotion/react */
import { css, CSSObject } from "@emotion/react";

import { useEffect, useRef, useState } from "react";

import { colors } from "@dotnet/telematics-design-system";

import FilterSeparator from "../../../../../../components/FilterSeparator/FilterSeparator";
import Separator from "../../../../../../components/Separator/Separator";
import { ROUTES, useMediaQueries } from "../../../../../../shared";
import ChartListItem from "../../../../components/ChartListItem/ChartListItem";
import { useStyles } from "../../../../styles";
import { CONNECTIVITY_TABS } from "../../../../types";
import useRenderCompaniesList from "../../../hooks/useRenderCompaniesList";
import { statusBorderColor, statusColor } from "../../functions";
import useDeviceStatusFunctions from "../../hooks/useDeviceStatusFunctions";
import { StatusSummaryType } from "../../types";

interface SingleStatusInfoProps {
  infoData: StatusSummaryType;
  hasCardWrapper: boolean;
  resourceDownloadLocation?: ROUTES;
  activeTab?: CONNECTIVITY_TABS;
}

const SingleStatusInfo = ({
  infoData,
  hasCardWrapper = true,
  resourceDownloadLocation,
  activeTab
}: SingleStatusInfoProps) => {
  const { toSm } = useMediaQueries();

  const { renderEmptyState } = useDeviceStatusFunctions();

  const { renderCompaniesList } = useRenderCompaniesList();

  const [scrolledToBottom, setScrolledToBottom] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);

  const {
    scrollableStatusInfoContainer,
    individualChartItemBox,
    bottomShadowBox,
    statusInfoListItem,
    singleStatusInfoBlock,
    divWithCenteredContent,
    statusInfoListItemWithoutWrapper,
    listRightArrow,
    hoverableListItem
  } = useStyles({
    toSm
  });

  const checkIfIsAtBottom = (element: EventTarget & HTMLDivElement) => {
    const isAtBottom =
      element.scrollHeight - element.scrollTop <= element.clientHeight + 1;

    setScrolledToBottom(isAtBottom);
  };

  useEffect(() => {
    const scrollElement = scrollRef.current;

    if (!scrollElement) {
      return;
    }

    checkIfIsAtBottom(scrollElement);
  }, [infoData.companies.length, toSm]);

  const isEmptyState = infoData.companies.length === 0;

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;

    checkIfIsAtBottom(target);
  };

  const listItemStyle = hasCardWrapper
    ? (statusInfoListItem as CSSObject)
    : (statusInfoListItemWithoutWrapper as CSSObject);

  return (
    <div
      css={css(singleStatusInfoBlock)}
      data-testid={`single-status-info-${infoData.name}`}
    >
      <div
        css={css([
          individualChartItemBox,
          { marginBottom: toSm ? "4px" : "unset" }
        ])}
      >
        <ChartListItem
          key={infoData.name}
          item={infoData}
          hoveredElement={null}
          hovered={false}
          backgroundColor={statusColor}
          borderColor={statusBorderColor}
        />
      </div>

      {toSm && (
        <Separator
          height="1px"
          color={colors.gray200}
          customStyle={{ width: "calc(100% - 16px)" }}
        />
      )}

      {!toSm && (
        <FilterSeparator
          isInitialLoading={false}
          width={1}
          height="100%"
          styles={{ zIndex: 10 }}
          light
        />
      )}

      <div
        ref={scrollRef}
        onScroll={handleScroll}
        css={css([
          scrollableStatusInfoContainer,
          { marginTop: toSm ? "16px" : "unset" }
        ])}
      >
        {isEmptyState
          ? renderEmptyState(divWithCenteredContent as CSSObject)
          : renderCompaniesList({
              infoData,
              statusInfoListItem: listItemStyle,
              hasCardWrapper,
              arrowStyle: listRightArrow as CSSObject,
              hoverableListItem: hoverableListItem as CSSObject,
              activeTab,
              resourceDownloadLocation
            })}
      </div>

      {!scrolledToBottom && <div css={css(bottomShadowBox)}></div>}
    </div>
  );
};

export default SingleStatusInfo;
